import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { SocialLinksEditor } from "@/components/builder/SocialLinksEditor";

export function ProfileSwitcher({ profiles, selectedId, onSelect, onCreate, onUpdate }) {
  const [dialogMode, setDialogMode] = useState(null);
  const [name, setName] = useState("");
  const [accentColor, setAccentColor] = useState("#6366F1");
  const [socialLinks, setSocialLinks] = useState([]);
  const current = profiles.find((p) => p.id === selectedId);

  useEffect(() => {
    if (dialogMode === "edit" && current) {
      setName(current.name || "");
      setAccentColor(current.accent_color || "#6366F1");
      setSocialLinks(current.social_links || []);
    }
  }, [dialogMode, current]);

  const openCreate = () => {
    setName("");
    setDialogMode("create");
  };

  const handleSubmit = async () => {
    if (dialogMode === "create") {
      if (!name.trim()) return;
      await onCreate(name.trim());
    } else {
      await onUpdate(selectedId, { name: name.trim(), accent_color: accentColor, social_links: socialLinks });
    }
    setDialogMode(null);
  };

  return (
    <div className="flex items-center gap-2" data-testid="profile-switcher">
      <Select value={selectedId} onValueChange={onSelect}>
        <SelectTrigger className="w-48 bg-white/5 border-white/15" data-testid="profile-select-trigger">
          <SelectValue placeholder="Select profile" />
        </SelectTrigger>
        <SelectContent>
          {profiles.map((p) => (
            <SelectItem key={p.id} value={p.id} data-testid={`profile-option-${p.id}`}>
              <span className="inline-flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: p.accent_color || "#6366F1" }} />
                {p.name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button onClick={() => setDialogMode("edit")} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10" title="Edit profile branding" data-testid="edit-profile-button">
        <i className="fa-solid fa-pen text-xs" />
      </button>
      <button onClick={openCreate} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10" title="Add artist profile" data-testid="add-profile-button">
        <i className="fa-solid fa-plus text-xs" />
      </button>

      <Dialog open={!!dialogMode} onOpenChange={(open) => !open && setDialogMode(null)}>
        <DialogContent className="bg-[#121824] border-white/10 text-white max-w-md" data-testid="profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{dialogMode === "create" ? "New Artist Profile" : "Edit Profile Branding"}</DialogTitle>
            <DialogDescription>
              {dialogMode === "create" ? "Create a separate profile to organize a different artist or alias." : "This profile's accent color and social links are inherited by every new page you create under it."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Profile Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Nova Ray, DJ Alias, Label Imprint..."
                className="bg-white/5 border-white/15"
                autoFocus
                data-testid="profile-name-input"
              />
            </div>
            {dialogMode === "edit" && (
              <>
                <div className="space-y-1.5">
                  <Label>Accent Color</Label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={accentColor}
                      onChange={(e) => setAccentColor(e.target.value)}
                      className="w-10 h-10 rounded-lg border border-white/15 bg-transparent cursor-pointer"
                      data-testid="profile-accent-color-input"
                    />
                    <Input value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className="bg-white/5 border-white/15 font-mono-slug" data-testid="profile-accent-color-text" />
                  </div>
                  <p className="text-xs text-slate-500">New pages created under this profile start with this accent color.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Default Social Links</Label>
                  <SocialLinksEditor links={socialLinks} onChange={setSocialLinks} />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button onClick={handleSubmit} className="bg-indigo-500 hover:bg-indigo-600" data-testid="profile-dialog-submit">
              {dialogMode === "create" ? "Create Profile" : "Save Branding"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
